import { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, MailX } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader } from '@reflex/ui/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import { useAuth } from '@/hooks/auth/useAuth';
import { useShowHiddenProviders } from '@/hooks/auth/useShowHiddenProviders';
import { SignInWithProviderButton } from '@/components/auth/SignInWithProviderButton';
import { DeclineInviteButton } from '@/components/invite/DeclineInviteButton';
import { api, ApiError, clearReturnPath, saveReturnPath } from '@/lib/api';
import { selectSignInProviders } from '@/lib/auth-providers';
import { orgBasePath, ROUTES } from '@/lib/routes';

const EMAIL_NOT_VERIFIED_MESSAGE =
  'Your email is not verified, so this invite cannot be accepted. Sign in with a provider that verifies your email address, such as GitHub or Google, using the invited address.';

/**
 * The invite routes refuse a session whose sign-in provider did not verify
 * the email (`403 { error: 'email_not_verified' }`): an invite grants org
 * access by email equality, so only a provider-verified address counts.
 */
function isEmailNotVerifiedError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && err.body.error === 'email_not_verified';
}

/**
 * Public landing page for invite share links. Renders metadata about the
 * invite (org, invited email, status). For the signed-in invitee it shows
 * an explicit "Accept invite" button (posts to `/invites/:inviteId/accept`)
 * alongside a confirm-guarded "Decline invite" action (posts to
 * `/invites/:inviteId/decline`); signed-out visitors get a "Sign in" CTA.
 *
 * Only OAuth providers are offered here. The email/password provider needs
 * the inline form the login screen contributes, and rendering it as a
 * redirect button pointed a signed-out invitee at the plugin's route prefix
 * instead of a sign-in flow, so this page leaves it out entirely.
 *
 * A signed-in user who is already a member of the invite's org is
 * redirected straight into that workspace, avoiding a misleading "already
 * accepted" dead-end. Declining flips the page to a terminal `declined`
 * card. Every terminal state (consumed/revoked/expired/declined/not_found)
 * carries an explicit exit CTA so the page is never a dead-end.
 */
export function InviteLandingPage() {
  const { token } = useParams<{ token: string }>();
  const {
    providers,
    authenticated,
    user,
    memberships,
    loading: authLoading,
    refreshSession,
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Per PRD-6 the return path exists only to survive the OAuth
    // round-trip. Signed out, we save this invite URL so the callback
    // returns here; once authenticated the round-trip is done and this
    // URL is a consumed dead-end, so drop it rather than let a later
    // return-path navigation (the onboarding "Continue" handler) bounce
    // the user back here.
    if (authenticated) clearReturnPath();
    else saveReturnPath();
  }, [authenticated, token]);

  const landingQuery = useQuery({
    queryKey: ['invites', 'landing', token],
    queryFn: () => api.getInviteLanding(token ?? ''),
    enabled: !!token,
    retry: false,
  });

  const showHidden = useShowHiddenProviders();
  const { oauth: oauthProviders } = selectSignInProviders(providers, showHidden);
  const data = landingQuery.data;

  const invitedEmail = data?.status === 'pending' ? data.invitedEmail : null;
  const matchesInvitedEmail =
    authenticated &&
    user?.email != null &&
    invitedEmail != null &&
    user.email.toLowerCase() === invitedEmail.toLowerCase();
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  // Set when the accept call comes back `email_not_verified`. The auth-status
  // flag below covers the same case up front; this catches a session whose
  // cached status predates the server's verdict.
  const [acceptRefusedUnverified, setAcceptRefusedUnverified] = useState(false);
  const emailNotVerified =
    authenticated &&
    (user?.emailVerified === false ||
      acceptRefusedUnverified ||
      isEmailNotVerifiedError(landingQuery.error));

  const handleAcceptInvite = async () => {
    if (accepting || !data || data.status !== 'pending') return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const response = await api.acceptInvite(data.inviteId);
      // `useAuth` holds memberships in its own `useReducer` state, not in
      // React Query, so no cache invalidation can make the new membership
      // visible to `OrgSlugResolver`. Await a
      // full `refreshSession()` so the freshly joined org is in `memberships`
      // *before* we navigate — otherwise the destination route flashes
      // through a 404 / one-shot self-heal cycle and the user lands back
      // on this page until they reload.
      await refreshSession();
      navigate(orgBasePath(response.joined.organizationSlug), { replace: true });
    } catch (err) {
      if (isEmailNotVerifiedError(err)) {
        setAcceptRefusedUnverified(true);
      } else {
        const message = err instanceof Error ? err.message : 'Failed to accept invite';
        setAcceptError(message);
      }
      setAccepting(false);
    }
  };

  const handleInviteDeclined = () => {
    // Re-fetch the landing metadata so the page flips to the terminal
    // `declined` card (with its exit CTA) instead of leaving the now-stale
    // Accept/Decline actions on screen.
    void landingQuery.refetch();
  };

  const handleSignOut = async () => {
    try {
      await api.logout();
    } finally {
      // Hard reload so the AuthGate re-evaluates and the OAuth
      // buttons reappear in their pristine state.
      window.location.reload();
    }
  };

  // While auth and the invite lookup are resolving we can't yet decide
  // whether to redirect a freshly-joined member, so treat both as a single
  // "loading" gate to avoid flashing a misleading terminal card.
  const resolving = landingQuery.isLoading || authLoading;

  // A signed-in user can land here already a member of the invite's org —
  // e.g. they accepted this invite earlier (explicitly, via the Accept
  // button or the org-setup prompt) and later reopened the link, at which
  // point the invite reads `consumed`. Sign-in itself never auto-joins
  // (acceptance is always explicit), so this is always the "already
  // accepted, revisiting the link" case. Send such a member straight into
  // their workspace instead of the dead-end "already accepted" card.
  const joinedOrg =
    data && data.status !== 'not_found'
      ? (memberships.find((m) => m.organization.id === data.organizationId) ?? null)
      : null;
  if (!resolving && authenticated && joinedOrg) {
    return <Navigate to={ROUTES.org(joinedOrg.organization.slug).runs} replace />;
  }

  // Every non-pending state is terminal; give it an explicit way out so the
  // page is never a dead-end. (The pending state carries its own sign-in /
  // accept / sign-out actions.)
  const showExitCta = !resolving && (!data || data.status !== 'pending');

  return (
    <div
      className="flex min-h-dvh items-center justify-center bg-background p-4"
      data-testid="invite-landing-page"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <PanelTitle>You&apos;ve been invited</PanelTitle>
          {/*
            Only show the "Sign in with <email>" caption to signed-out
            visitors. For signed-in users we know either:
              - their email matches → the Accept-invite branch below
                carries the right copy
              - or their email doesn't → mismatch alert below carries
                the correct copy.
          */}
          {!resolving && !authenticated && data && data.status === 'pending' && (
            <CardDescription>
              Sign in with <strong>{data.invitedEmail}</strong> to join{' '}
              <strong>{data.organizationName}</strong>.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent
          className="flex flex-col gap-4"
          data-testid={`invite-landing-status-${data?.status ?? (landingQuery.isLoading ? 'loading' : 'unknown')}`}
        >
          {resolving && (
            <p className="text-center text-sm text-muted-foreground">Loading invite&hellip;</p>
          )}

          {!resolving && !data && emailNotVerified && (
            <InviteFailureMessage
              icon={<MailX className="size-5" />}
              title="Email not verified"
              description={EMAIL_NOT_VERIFIED_MESSAGE}
            />
          )}

          {!resolving && ((!data && !emailNotVerified) || data?.status === 'not_found') && (
            <InviteFailureMessage
              icon={<MailX className="size-5" />}
              title="Invite not found"
              description="This invite link is invalid or has already been removed. Ask the person who invited you for a fresh link."
            />
          )}

          {!resolving && data && data.status === 'pending' && (
            <>
              <div className="flex items-center justify-center gap-3 py-2">
                {data.organizationAvatarUrl ? (
                  <img
                    src={data.organizationAvatarUrl}
                    alt=""
                    className="size-10 rounded-md object-cover"
                  />
                ) : (
                  <div className="flex size-10 items-center justify-center rounded-md bg-muted text-sm font-medium text-muted-foreground">
                    {data.organizationName.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="text-left">
                  <p className="text-base font-medium">{data.organizationName}</p>
                  <p className="text-xs text-muted-foreground">{data.invitedEmail}</p>
                </div>
              </div>

              {authenticated && matchesInvitedEmail && emailNotVerified ? (
                <div
                  className="flex flex-col gap-3"
                  data-testid="invite-landing-email-not-verified"
                >
                  <Alert>
                    <MailX className="size-4" />
                    <AlertTitle>Email not verified</AlertTitle>
                    <AlertDescription>{EMAIL_NOT_VERIFIED_MESSAGE}</AlertDescription>
                  </Alert>
                  <Button
                    variant="outline"
                    onClick={handleSignOut}
                    data-testid="invite-landing-sign-out"
                  >
                    Sign out and choose a provider
                  </Button>
                </div>
              ) : authenticated && matchesInvitedEmail ? (
                <div className="flex flex-col gap-3" data-testid="invite-landing-matching-email">
                  <Button
                    onClick={handleAcceptInvite}
                    disabled={accepting}
                    data-testid="invite-landing-accept-button"
                  >
                    {accepting
                      ? `Joining ${data.organizationName}…`
                      : `Accept invite to ${data.organizationName}`}
                  </Button>
                  <DeclineInviteButton
                    inviteId={data.inviteId}
                    organizationName={data.organizationName}
                    onDeclined={handleInviteDeclined}
                    triggerLabel="Decline invite"
                    triggerVariant="outline"
                    triggerSize="default"
                    testId="invite-landing-decline"
                  />
                  {acceptError && (
                    <Alert variant="destructive">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Could not accept invite</AlertTitle>
                      <AlertDescription>{acceptError}</AlertDescription>
                    </Alert>
                  )}
                </div>
              ) : authenticated ? (
                <div className="flex flex-col gap-3" data-testid="invite-landing-wrong-email">
                  <Alert variant="destructive">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Wrong account</AlertTitle>
                    <AlertDescription>
                      <p>
                        You&apos;re signed in as <strong>{user?.email}</strong>, but this invite is
                        for <strong>{data.invitedEmail}</strong>. Sign out and sign back in with{' '}
                        <strong>{data.invitedEmail}</strong> to accept.
                      </p>
                    </AlertDescription>
                  </Alert>
                  <Button
                    variant="outline"
                    onClick={handleSignOut}
                    data-testid="invite-landing-sign-out"
                  >
                    Sign out
                  </Button>
                </div>
              ) : oauthProviders.length === 0 ? (
                <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>No sign-in providers configured</AlertTitle>
                  <AlertDescription>
                    Ask your administrator to enable an OAuth provider so you can accept this
                    invite.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="flex flex-col gap-2">
                  {oauthProviders.map(([name, { label }], index) => (
                    <SignInWithProviderButton
                      key={name}
                      name={name}
                      label={label}
                      variant={index === 0 ? 'primary' : 'outline'}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {!resolving && data && data.status === 'consumed' && (
            <InviteFailureMessage
              icon={<CheckCircle2 className="size-5" />}
              title="Invite already accepted"
              description={
                authenticated
                  ? `This invite for ${data.invitedEmail} has already been accepted.`
                  : `This invite for ${data.invitedEmail} has already been accepted. Sign in to access ${data.organizationName}.`
              }
            />
          )}

          {!resolving && data && data.status === 'revoked' && (
            <InviteFailureMessage
              icon={<MailX className="size-5" />}
              title="Invite revoked"
              description={`The administrators of ${data.organizationName} revoked this invite. Ask them to send a new one.`}
            />
          )}

          {!resolving && data && data.status === 'expired' && (
            <InviteFailureMessage
              icon={<AlertCircle className="size-5" />}
              title="Invite expired"
              description={`This invite for ${data.organizationName} expired. Ask for a fresh invite link.`}
            />
          )}

          {!resolving && data && data.status === 'declined' && (
            <InviteFailureMessage
              icon={<MailX className="size-5" />}
              title="Invite declined"
              description={`You declined this invite to ${data.organizationName}. Ask an admin to send a new one if you'd like to join.`}
            />
          )}

          {showExitCta && (
            <Button
              variant="outline"
              onClick={() => navigate(ROUTES.root)}
              data-testid="invite-landing-exit-cta"
            >
              {authenticated ? 'Go to your workspace' : 'Sign in'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface InviteFailureMessageProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function InviteFailureMessage({ icon, title, description }: InviteFailureMessageProps) {
  return (
    <Alert>
      {icon}
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{description}</AlertDescription>
    </Alert>
  );
}
